import { useState } from "react";
import { Clock, X } from "lucide-react";

const hours = Array.from({ length: 12 }, (_, i) => i + 1);
const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

export const TimePicker = ({ value, onChange, testId }) => {
  const [open, setOpen] = useState(false);

  const parsed = (() => {
    if (!value) return { h: "12", m: "00", p: "AM" };
    const [hh, mm] = value.split(":");
    let h = parseInt(hh, 10);
    const p = h >= 12 ? "PM" : "AM";
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return { h: String(h), m: mm, p };
  })();

  const [tempH, setTempH] = useState(parsed.h);
  const [tempM, setTempM] = useState(parsed.m);
  const [tempP, setTempP] = useState(parsed.p);

  const openPicker = () => {
    setTempH(parsed.h);
    setTempM(parsed.m);
    setTempP(parsed.p);
    setOpen(true);
  };

  const confirm = () => {
    let hour24 = parseInt(tempH, 10);
    if (tempP === "AM" && hour24 === 12) hour24 = 0;
    else if (tempP === "PM" && hour24 !== 12) hour24 += 12;
    onChange(`${String(hour24).padStart(2, "0")}:${tempM}`);
    setOpen(false);
  };

  const display = value ? `${parsed.h}:${parsed.m} ${parsed.p}` : "";

  return (
    <>
      <div
        onClick={openPicker}
        data-testid={testId}
        className="flex items-center h-11 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm cursor-pointer hover:border-stone-400 transition-colors"
      >
        <Clock className="w-4 h-4 text-stone-400 mr-2 flex-shrink-0" />
        {display ? (
          <span className="text-stone-800">{display}</span>
        ) : (
          <span className="text-stone-400">Select time</span>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs overflow-hidden animate-in fade-in zoom-in-95" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-primary px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-white" />
                <span className="text-white font-heading font-semibold">Select Time</span>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Preview */}
            <div className="bg-stone-900 px-4 py-4 text-center">
              <p className="text-4xl font-mono font-bold text-white tracking-wider">
                {tempH}:{tempM} <span className="text-2xl text-primary">{tempP}</span>
              </p>
            </div>

            {/* Picker columns */}
            <div className="grid grid-cols-3 gap-0 p-3">
              {/* Hours */}
              <div>
                <p className="text-[10px] font-bold text-stone-400 uppercase mb-2 text-center tracking-widest">Hour</p>
                <div className="max-h-44 overflow-y-auto space-y-0.5 px-1 scroll-smooth">
                  {hours.map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => setTempH(String(h))}
                      className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        tempH === String(h)
                          ? "bg-primary text-white shadow-md scale-105"
                          : "hover:bg-stone-100 text-stone-700"
                      }`}
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>

              {/* Minutes */}
              <div>
                <p className="text-[10px] font-bold text-stone-400 uppercase mb-2 text-center tracking-widest">Min</p>
                <div className="max-h-44 overflow-y-auto space-y-0.5 px-1 scroll-smooth">
                  {minutes.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setTempM(m)}
                      className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        tempM === m
                          ? "bg-primary text-white shadow-md scale-105"
                          : "hover:bg-stone-100 text-stone-700"
                      }`}
                    >
                      :{m}
                    </button>
                  ))}
                </div>
              </div>

              {/* AM/PM */}
              <div>
                <p className="text-[10px] font-bold text-stone-400 uppercase mb-2 text-center tracking-widest">Period</p>
                <div className="space-y-2 px-1">
                  {["AM", "PM"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setTempP(p)}
                      className={`w-full py-4 rounded-xl text-sm font-bold transition-all ${
                        tempP === p
                          ? "bg-primary text-white shadow-md scale-105"
                          : "hover:bg-stone-100 text-stone-600 border border-stone-200"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Confirm button */}
            <div className="p-3 pt-0">
              <button
                onClick={confirm}
                data-testid="confirm-time-btn"
                className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl py-3 font-semibold text-sm transition-colors"
              >
                Set {tempH}:{tempM} {tempP}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TimePicker;
